import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Navigate, useMatchRoute, useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';

import { useIssueCountLiveUpdate } from '../../entities/issue';
import { useNotificationDigest } from '../../features/notification-feed';
import { signOut, switchOrganization, useSession } from '../../entities/session';
import { ApiError } from '../../shared/api/errors';
import { CommandPalette, type TNewFlowPath } from '../command-palette';
import { FlowCreate } from '../flow-create';
import { OrganizationCreate } from '../../features/organization-create';
import { ErrorState, LoadingState } from '../../shared/ui/states';
import { Chord } from './ui/chord';
import { NAV_ENTRIES, SHELL_CHORDS } from './ui/nav';
import { ShortcutsSheet } from './ui/shortcuts-sheet';
import { Sidebar, type TShellMenu } from './ui/sidebar';
import type { TTheme } from './ui/user-menu';
import { useSidebarMode } from './sidebar-mode';

const THEME_KEY = 'hp.theme';

const applyTheme = (theme: TTheme) => {
  const resolved =
    theme === 'system'
      ? window.matchMedia('(prefers-color-scheme: light)').matches
        ? 'light'
        : 'dark'
      : theme;
  document.documentElement.dataset.theme = resolved;
};

export const AppShell = ({ children }: { children: ReactNode }) => {
  const session = useSession();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searching, setSearching] = useState(false);
  const [sheet, setSheet] = useState(false);
  const [menu, setMenu] = useState<TShellMenu>(null);
  const [creatingOrganization, setCreatingOrganization] = useState(false);
  const newFlow = useRef<((path: TNewFlowPath) => void) | null>(null);
  const [theme, setTheme] = useState<TTheme>(
    () => (localStorage.getItem(THEME_KEY) as TTheme | null) ?? 'system',
  );

  useEffect(() => {
    applyTheme(theme);
    localStorage.setItem(THEME_KEY, theme);
  }, [theme]);

  const matchRoute = useMatchRoute();
  const sidebar = useSidebarMode(Boolean(matchRoute({ to: '/flows/$flowId' })));
  const issueCount = useIssueCountLiveUpdate({ enabled: session.isSuccess });
  const notifications = useNotificationDigest();

  const chords = (
    <>
      <Chord
        hotkey={SHELL_CHORDS.search.hotkey}
        inFields
        onPress={() => setSearching((open) => !open)}
      />
      <Chord hotkey={SHELL_CHORDS.shortcuts.hotkey} onPress={() => setSheet(true)} />
      <Chord hotkey={SHELL_CHORDS.sidebar.hotkey} inFields onPress={sidebar.toggle} />
      {NAV_ENTRIES.map((entry) => (
        <Chord
          key={entry.to}
          hotkey={entry.hotkey}
          onPress={() => void navigate({ to: entry.to })}
        />
      ))}
      <Chord
        hotkey={SHELL_CHORDS.settings.hotkey}
        onPress={() => void navigate({ to: '/settings' })}
      />
      <Chord hotkey={SHELL_CHORDS.notifications.hotkey} onPress={() => setMenu('notifications')} />
      <Chord hotkey={SHELL_CHORDS.organizations.hotkey} onPress={() => setMenu('organizations')} />
      <Chord hotkey={SHELL_CHORDS.account.hotkey} onPress={() => setMenu('account')} />
    </>
  );

  if (session.isPending) {
    return (
      <div className="p-page">
        <LoadingState rows={6} />
      </div>
    );
  }

  if (session.isError) {
    // The route guard only sees whether a token exists; a 401 here is the dead one, and it is a sign-in rather than an error to read.
    if (session.error instanceof ApiError && session.error.status === 401) {
      return <Navigate to="/sign-in" search={{ next: window.location.href }} replace />;
    }

    return (
      <div className="p-page">
        <ErrorState error={session.error} onRetry={() => void session.refetch()} />
      </div>
    );
  }

  const { user, memberships, preferences } = session.data;
  const currentMembership =
    memberships.find((membership) => membership.isCurrent) ?? memberships[0];
  const current = currentMembership
    ? {
        id: currentMembership.organizationId,
        name: currentMembership.organizationName,
        role: currentMembership.role,
      }
    : { id: '', name: 'No organization' };

  return (
    <div className="flex h-full">
      {chords}
      <Sidebar
        current={current}
        organizations={memberships.map((membership) => ({
          id: membership.organizationId,
          name: membership.organizationName,
          role: membership.role,
        }))}
        onSwitch={async (id) => {
          await switchOrganization(id);
          await queryClient.invalidateQueries();
        }}
        onCreateOrganization={() => setCreatingOrganization(true)}
        onSearch={() => setSearching(true)}
        issueCount={issueCount}
        notifications={notifications}
        timeZone={preferences.timezone}
        menu={menu}
        onMenu={setMenu}
        collapsed={sidebar.collapsed}
        onToggle={sidebar.toggle}
        user={{
          name: user.name,
          email: user.email,
          timeZone: preferences.timezone,
          theme,
          onTheme: setTheme,
          onAccount: () => void navigate({ to: '/settings/account' }),
          onShortcuts: () => setSheet(true),
          onSignOut: () => {
            signOut();
            queryClient.clear();
            void navigate({ to: '/sign-in' });
          },
        }}
      />

      <div className="flex min-w-0 grow flex-col overflow-y-auto">{children}</div>

      <CommandPalette
        open={searching}
        onOpenChange={setSearching}
        onNewFlow={(path) => newFlow.current?.(path)}
      />

      {/* Mounted once here so the palette's "New flow" opens the same dialog the Flows page does. */}
      <FlowCreate
        onCreated={(flow) => void navigate({ to: '/flows/$flowId', params: { flowId: flow.id } })}
        renderTrigger={(openAt) => {
          newFlow.current = openAt;
          return null;
        }}
      />

      <ShortcutsSheet open={sheet} onOpenChange={setSheet} />

      <OrganizationCreate
        open={creatingOrganization}
        onOpenChange={setCreatingOrganization}
        onCreated={() => void navigate({ to: '/flows' })}
      />
    </div>
  );
};
